
import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import DialogModal from '../components/DialogModal';

export type DialogType = 'alert' | 'confirm' | 'prompt';

export interface DialogOptions {
  title?: string;
  message: string;
  type: DialogType;
  confirmText?: string;
  cancelText?: string;
  defaultValue?: string;
  placeholder?: string;
  destructive?: boolean; // For red delete buttons
}

interface DialogContextType {
  alert: (message: string, options?: Omit<DialogOptions, 'message' | 'type'>) => Promise<void>;
  confirm: (message: string, options?: Omit<DialogOptions, 'message' | 'type'>) => Promise<boolean>;
  prompt: (message: string, options?: Omit<DialogOptions, 'message' | 'type'>) => Promise<string | null>;
}

const DialogContext = createContext<DialogContextType | undefined>(undefined);

export const DialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<DialogOptions>({ message: '', type: 'alert' });
  const resolveRef = useRef<((value: any) => void) | null>(null);

  const open = useCallback((options: DialogOptions) => {
    setConfig(options);
    setIsOpen(true);
    return new Promise<any>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const close = useCallback((value: any) => {
    setIsOpen(false);
    if (resolveRef.current) {
      resolveRef.current(value);
      resolveRef.current = null;
    }
  }, []);

  const alert = useCallback(async (message: string, options?: Omit<DialogOptions, 'message' | 'type'>) => {
    await open({ message, type: 'alert', confirmText: '확인', ...options });
  }, [open]);

  const confirm = useCallback(async (message: string, options?: Omit<DialogOptions, 'message' | 'type'>) => {
    return await open({ message, type: 'confirm', confirmText: '확인', cancelText: '취소', ...options });
  }, [open]);

  const prompt = useCallback(async (message: string, options?: Omit<DialogOptions, 'message' | 'type'>) => {
    return await open({ message, type: 'prompt', confirmText: '확인', cancelText: '취소', ...options });
  }, [open]);

  return (
    <DialogContext.Provider value={{ alert, confirm, prompt }}>
      {children}
      {isOpen && <DialogModal config={config} onClose={close} />}
    </DialogContext.Provider>
  );
};

export const useDialog = () => {
  const context = useContext(DialogContext);
  if (!context) throw new Error('useDialog must be used within a DialogProvider');
  return context;
};
